import {
  Box,
  Button,
  Typography,
  TextField,
  useMediaQuery,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import { useState, useEffect } from "react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useHotel } from "../context/HotelContext";
import LoadingGif from "../components/LoadingGif";

const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const DISTRICT_KEYS = [
  "Central and Western District",
  "Wan Chai District",
  "Eastern District",
  "Southern District",
  "Yau Tsim Mong District",
  "Sham Shui Po District",
  "Kowloon City District",
  "Wong Tai Sin District",
  "Kwun Tong District",
  "Tsuen Wan District",
  "Tuen Mun District",
  "Yuen Long District",
  "North District",
  "Tai Po District",
  "Sha Tin District",
  "Sai Kung District",
  "Kwai Tsing District",
  "Islands District",
];

const HotelAdd_Information_3 = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const edit = searchParams.get('edit') || '';
  const { id } = useParams();
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { hotelData, loading, saveStep, fetchDraft } = useHotel();
  const [formData, setFormData] = useState({
    hotelName: "",
    address: "",
    intro: "",
    district: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id && !hotelData.basicInfo) {
      fetchDraft(id);
    }
  }, [id, hotelData.basicInfo, fetchDraft]);

  useEffect(() => {
    if (hotelData.basicInfo) {
      setFormData(hotelData.basicInfo);
    }
  }, [hotelData.basicInfo]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNext = async () => {
    try {
      setIsSaving(true);
      await saveStep("basicInfo", formData);
      navigate(edit ? `/hotel/${id}/information` : `/hotel/add/info4/${id}`);
    } catch (err) {
      console.error("Failed to save step:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      await saveStep("basicInfo", formData);
      navigate(`/hotel/onboarding`);
    } catch (err) {
      console.error("Failed to save step:", err);
    }
  }; 

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto" }}>
          {STEPS.map((step, index) => (
            <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: isMobile ? "20px" : "30px",
                    height: isMobile ? "20px" : "30px",
                    borderRadius: "50%",
                    backgroundColor: index === 0 ? "#31BACD" : "transparent",
                    border: index === 0 ? "3px solid #fff" : "2px solid #D1D1D1",
                    boxShadow: index === 0 ? "0 0 0 2px #31BACD" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "13px",
                    fontWeight: index === 0 ? "600" : "400",
                    color: index === 0 ? "#000" : "#666",
                    textAlign: "center",
                    whiteSpace: "wrap",
                    width: isMobile ? "50px" : "100px",
                  }}
                >
                  {t(step.labelKey)}
                </Typography>
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    height: "1px",
                    width: isMobile ? "30px" : "200px",
                    backgroundColor: "#D1D1D1",
                    mx: "auto",
                    mb: isMobile ? "18px" : "24px",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          {/* Heading */}
          <Box sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: isMobile ? "20px" : "24px",
                color: "#000",
                mb: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {t('HotelInfoStep3Title')}
              <Box component="span" sx={{ fontWeight: "400", fontSize: isMobile ? "12px" : "14px", color: "#666" }}>
                {t('MustFill')} <span style={{ color: "red" }}>*</span><span> )</span>
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: isMobile ? "13px" : "14px",
                color: "#666",
              }}
            >
              {t('HotelInfoStep3Desc')}
            </Typography>
          </Box>

          {/* Form fields */}
          {loading ? (
            <LoadingGif />
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 4, mt: 4, width: "100%" }}>
              <Box>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                  {t('HotelNameLabel')}
                  <span style={{ color: "red" }}> *</span>
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={formData.hotelName}
                  placeholder={t('HotelNamePlaceholder')}
                  onChange={(e) => handleInputChange("hotelName", e.target.value)}
                  InputProps={{
                    sx: {
                      bgcolor: "white",
                      borderRadius: "10px",
                      height: isMobile ? "45px" : "56px",
                      fontSize: isMobile ? "14px" : "16px",
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1 }}>
                  {t('HotelAddressLabel')}
                  <span style={{ color: "red" }}> *</span>
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexDirection: isMobile ? "column" : "row", width: "100%" }}>
                  <Box sx={{ flex: isMobile ? "1" : "0 0 20%" }}>
                    <Select
                      fullWidth
                      value={formData.district}
                      onChange={(e) => handleInputChange("district", e.target.value)}
                      displayEmpty
                      sx={{
                        bgcolor: "white",
                        borderRadius: "10px",
                        height: isMobile ? "45px" : "56px",
                        fontSize: isMobile ? "14px" : "16px",
                      }}
                    >
                      <MenuItem value="" disabled>
                        {t('SelectDistrict')}
                      </MenuItem>
                      {DISTRICT_KEYS.map((key) => (
                        <MenuItem key={key} value={key}>
                          {t(key)}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Box sx={{ flex: "1" }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={formData.address}
                      placeholder={t('HotelAddressPlaceholder')}
                      onChange={(e) => handleInputChange("address", e.target.value)}
                      InputProps={{
                        sx: {
                          bgcolor: "white",
                          borderRadius: "10px",
                          height: isMobile ? "45px" : "56px",
                          fontSize: isMobile ? "14px" : "16px",
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 0.5 }}>
                  {t('HotelIntroLabel')} <span style={{ color: "red" }}>*</span>
                </Typography>
                <Typography sx={{ fontSize: isMobile ? "12px" : "14px", color: "#666", mb: 1 }}>
                  {t('HotelIntroDesc')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  placeholder={t('HotelInfoPlaceholder')}
                  value={formData.intro}
                  onChange={(e) => handleInputChange("intro", e.target.value)}
                  InputProps={{
                    sx: {
                      bgcolor: "white",
                      borderRadius: "10px",
                      fontSize: isMobile ? "14px" : "16px",
                    },
                  }}
                />
              </Box>
            </Box>
          )}
          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={!formData.hotelName.trim() || !formData.address.trim() || !formData.district.trim() || !formData.intro.trim() || loading}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate(-1)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={!formData.hotelName.trim() || !formData.address.trim() || !formData.district.trim() || !formData.intro.trim() || isSaving}
                onClick={handleNext}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />
    </div>
  );
};

export default HotelAdd_Information_3;

